import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { FrameTemplate, CreateFrameTemplate, UpdateFrameTemplate } from '@estimator/shared';

export function useFrameTemplates() {
  return useQuery({
    queryKey: ['frame-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<FrameTemplate[]>('/frame-templates');
      return data;
    },
  });
}

export function useFrameTemplate(id: string | null) {
  return useQuery({
    queryKey: ['frame-templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<FrameTemplate>(`/frame-templates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateFrameTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFrameTemplate) => {
      const { data } = await apiClient.post<FrameTemplate>('/frame-templates', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['frame-templates'] }),
  });
}

export function useUpdateFrameTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateFrameTemplate & { id: string }) => {
      const { data } = await apiClient.patch<FrameTemplate>(`/frame-templates/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['frame-templates'] });
      queryClient.invalidateQueries({ queryKey: ['frame-templates', vars.id] });
    },
  });
}

export function useDeleteFrameTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/frame-templates/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['frame-templates'] }),
  });
}
